'use client'

import { Box, Flex, Heading, Text, Icon } from '@chakra-ui/react'
import { FC } from 'react'
import { FaCheck } from 'react-icons/fa'
import Link from 'next/link'

export type StoryCardProps = {
  tools: string[]
  title: string
  problem: string
  outcome: string
  href?: string
}

export const StoryCard: FC<StoryCardProps> = ({ tools, title, problem, outcome, href }) => {
  const cardContent = (
    <Box
      as="article"
      bg="background.surface"
      borderRadius="card"
      p={{ base: 6, md: 10 }}
      border="1px solid"
      borderColor="border.default"
      transition="all 0.3s ease"
      position="relative"
      overflow="hidden"
      role="group"
      cursor={href ? 'pointer' : 'default'}
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        bg: 'teal.500',
        transform: 'scaleX(0)',
        transformOrigin: 'left',
        transition: 'transform 0.3s ease'
      }}
      _hover={{
        borderColor: 'teal.500',
        boxShadow: 'cardHover',
        transform: 'translateY(-4px)',
        _before: {
          transform: 'scaleX(1)'
        }
      }}
    >
      <Flex flexWrap="wrap" gap={1.5} mb={6}>
        {tools.map((tool, index) => (
          <Box
            key={index}
            as="span"
            fontSize="0.75rem"
            fontWeight={500}
            px={3}
            py={1}
            bg="background.default"
            borderRadius="sm"
            color="text.secondary"
          >
            {tool}
          </Box>
        ))}
      </Flex>

      <Heading
        as="h3"
        fontFamily="display"
        fontSize="1.4rem"
        fontWeight={500}
        lineHeight={1.3}
        mb={4}
        color="text.default"
      >
        {title}
      </Heading>

      <Text fontSize="0.95rem" color="text.secondary" mb={6} lineHeight={1.6}>
        {problem}
      </Text>

      <Flex align="flex-start" gap={2} fontSize="0.9rem" fontWeight={500} color="teal.500">
        <Icon as={FaCheck} flexShrink={0} mt={1} />
        <Text>{outcome}</Text>
      </Flex>
    </Box>
  )

  if (href) {
    return <Link href={href}>{cardContent}</Link>
  }

  return cardContent
}
